import { diffChars } from "diff";
import type { Change } from "diff";

import { formatScheduleLengthFromLineTotals } from "./lib/scene-page-length-options";
import { PARAGRAPH_TYPE_OPTIONS } from "./constants";
import {
  DraftParagraph,
  DraftScene,
  DraftTagSpan,
  ScreenplayParagraphType,
  type TagCategory,
  type TagSelection,
} from "./types";

const KNOWN_PARAGRAPH_TYPES = new Set<ScreenplayParagraphType>(PARAGRAPH_TYPE_OPTIONS);

/** Matches backend `paragraphs.paragraph_uid` (char 21) and draft DTO `@Length(1, 21)`. */
const PARAGRAPH_UID_ALPHABET = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";

export function generateParagraphUid(): string {
  const bytes = new Uint8Array(21);
  if (typeof crypto !== "undefined" && "getRandomValues" in crypto) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 21; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  let out = "";
  for (let i = 0; i < 21; i += 1) {
    out += PARAGRAPH_UID_ALPHABET[bytes[i]! % PARAGRAPH_UID_ALPHABET.length];
  }
  return out;
}

function coerceScreenplayParagraphType(type: string): ScreenplayParagraphType {
  return KNOWN_PARAGRAPH_TYPES.has(type as ScreenplayParagraphType)
    ? (type as ScreenplayParagraphType)
    : "general";
}

const TAG_SPAN_WORD_CHAR = /^[\p{L}\p{N}']+$/u;
const TAG_SPAN_LETTER_RUN = /^([\p{L}\p{N}']+)/u;
const NON_WORD_ALNUM_FOR_FOLD = /[^\p{L}\p{N}']/gu;

function foldTagLabelForMatch(s: string): string {
  return s.normalize("NFKC").replace(NON_WORD_ALNUM_FOR_FOLD, "").toLowerCase();
}

function canonicalSingleTokenKeysForSpan(span: DraftTagSpan): ReadonlySet<string> {
  const keys = new Set<string>();
  for (const src of [span.tag_merged_label, span.label, span.tag_display_name]) {
    if (typeof src !== "string" || !src.trim()) continue;
    const trimmed = src.trim();
    if (/\s/.test(trimmed)) continue;
    const k = foldTagLabelForMatch(trimmed);
    if (k.length > 0) keys.add(k);
  }
  return keys;
}

function spanNamingMetadataIncludesWhitespace(span: DraftTagSpan): boolean {
  for (const src of [span.tag_merged_label, span.label, span.tag_display_name]) {
    if (typeof src !== "string" || !src.trim()) continue;
    if (/\s/.test(src.trim())) return true;
  }
  return false;
}

/** Simple Title Case for Latin script (e.g. `Row`; not McDonald-style prefixes). Apostrophes allowed inside `prefix`. */
function isStrictTitleCaseWord(prefix: string): boolean {
  if (prefix.length < 2 || prefix.length > 48) return false;
  if (!/^[\p{L}][\p{L}\p{N}']*$/u.test(prefix)) return false;
  const first = prefix.slice(0, 1).toLocaleUpperCase("en-US");
  const rest = prefix.slice(1).toLocaleLowerCase("en-US");
  return prefix === first + rest;
}

const HEAL_REJECT_TWO_LETTER_LOWER_SUFFIX = new Set([
  "at",
  "be",
  "do",
  "go",
  "if",
  "in",
  "is",
  "it",
  "no",
  "of",
  "ok",
  "on",
  "or",
  "ox",
  "so",
  "to",
  "up",
  "we",
]);

const HEAL_REJECT_THREE_LETTER_LOWER_SUFFIX = new Set(["and", "but", "for", "nor", "the", "yet"]);

/** When spans carry no usable single-token label keys (API gaps / labels with whitespace like "Row x2"), infer same-word glue. */
function heuristicGlueAfterTaggedPrefix(
  paragraph: DraftParagraph,
  span: DraftTagSpan,
  prefix: string,
  continuation: string
): boolean {
  if (spanNamingMetadataIncludesWhitespace(span)) return false;

  const contLen = continuation.length;
  if (contLen < 1 || contLen > 3) return false;

  const typ = paragraph.type;

  const actionLike = typ === "action" || typ === "general";

  if (actionLike) {
    if (!/^[a-z]+$/u.test(continuation)) return false;
    if (contLen === 2 && HEAL_REJECT_TWO_LETTER_LOWER_SUFFIX.has(continuation)) return false;
    if (contLen === 3 && HEAL_REJECT_THREE_LETTER_LOWER_SUFFIX.has(continuation)) return false;
    return isStrictTitleCaseWord(prefix);
  }

  if (typ === "character") {
    if (prefix.length < 2 || prefix.length > 32) return false;
    return (
      /^[A-Z][A-Z0-9']*$/u.test(prefix) &&
      /^[A-Z][A-Z0-9']*$/u.test(continuation) &&
      continuation.length >= 2
    );
  }

  return false;
}

/**
 * Collapses `Prefix␠Suffix` right after a tag span when suffix completes one tagged lexeme — either backed
 * by single-token labels, or heuristically when metadata has no keys (Yjs/collab split like `Row␠en`
 * or `ROW␠EN`). Preserves intentional multi-token names when label metadata includes whitespace.
 */
export function healSpuriousSpaceAfterSplitTagSpan(paragraph: DraftParagraph): DraftParagraph {
  if (typeof paragraph.source_text_preserved === "boolean") return paragraph;
  let text = typeof paragraph.text === "string" ? paragraph.text : "";
  const rawSpans = Array.isArray(paragraph.tag_spans) ? paragraph.tag_spans : [];
  if (!text.length || rawSpans.length === 0) return paragraph;

  let spans = rawSpans.map((s) => ({ ...s }));

  let changed = true;
  while (changed) {
    changed = false;
    const ordered = spans
      .map((span, index) => ({ span, index }))
      .sort((a, b) => b.span.end_offset - a.span.end_offset);

    for (const { span, index } of ordered) {
      const keys = canonicalSingleTokenKeysForSpan(span);

      const s = span.start_offset;
      const e = span.end_offset;
      if (s < 0 || e <= s || e >= text.length) continue;
      if (text[e] !== " ") continue;

      const prefix = text.slice(s, e);
      if (!TAG_SPAN_WORD_CHAR.test(prefix)) continue;

      const after = text.slice(e + 1);
      const continuation = TAG_SPAN_LETTER_RUN.exec(after)?.[1] ?? "";
      if (!continuation.length) continue;

      const tail = e + 1 + continuation.length;
      if (tail < text.length && /[\p{L}\p{N}']/u.test(text[tail]!)) continue;

      const glued = foldTagLabelForMatch(prefix + continuation);
      const labelGlue = keys.size > 0 && keys.has(glued);
      const heuristicGlue =
        keys.size === 0 && heuristicGlueAfterTaggedPrefix(paragraph, span, prefix, continuation);
      const shouldGlue = labelGlue || heuristicGlue;

      if (!shouldGlue) continue;

      text = text.slice(0, e) + text.slice(e + 1);
      const newEnd = e + continuation.length;
      spans[index] = { ...span, end_offset: newEnd };

      for (let i = 0; i < spans.length; i += 1) {
        if (i === index) continue;
        const o = spans[i]!;
        let ns = o.start_offset;
        let ne = o.end_offset;
        if (ns > e) ns -= 1;
        if (ne > e) ne -= 1;
        spans[i] = { ...o, start_offset: ns, end_offset: ne };
      }

      changed = true;
      break;
    }
  }

  return { ...paragraph, text, tag_spans: mergeAdjacentTagSpans(spans) };
}

/** Ensures editor + inspector never crash on partial API or recovery payloads. */
export function normalizeDraftParagraphsForEditor(paragraphs: DraftParagraph[]): DraftParagraph[] {
  const seenParagraphIds = new Set<number>();
  const deduped = paragraphs.filter((paragraph) => {
    if (seenParagraphIds.has(paragraph.paragraph_id)) return false;
    seenParagraphIds.add(paragraph.paragraph_id);
    return true;
  });
  return deduped.map((paragraph, index) => {
    const uid = paragraph.paragraph_uid?.trim();
    const base: DraftParagraph = {
      ...paragraph,
      sort_order: index,
      type: coerceScreenplayParagraphType(paragraph.type),
      text: typeof paragraph.text === "string" ? paragraph.text : "",
      tag_spans: Array.isArray(paragraph.tag_spans) ? paragraph.tag_spans : [],
      paragraph_uid: uid && uid.length > 0 ? uid : generateParagraphUid(),
    };
    return healSpuriousSpaceAfterSplitTagSpan(base);
  });
}

/**
 * Stable body signature for autosave / dirty detection: type, text, order, ids, and tag geometry.
 * Ignores projection-only fields (labels, merged names). Not affected by pagination or layout.
 */
export function draftParagraphsPersistFingerprint(rows: DraftParagraph[]): string {
  return JSON.stringify(
    rows.map((p, index) => ({
      id: p.paragraph_id,
      uid: (p.paragraph_uid ?? "").trim(),
      i: index,
      ty: coerceScreenplayParagraphType(p.type),
      t: typeof p.text === "string" ? p.text : "",
      tags: [...(Array.isArray(p.tag_spans) ? p.tag_spans : [])]
        .sort(
          (a, b) =>
            a.start_offset - b.start_offset ||
            a.end_offset - b.end_offset ||
            a.tag_definition_id - b.tag_definition_id ||
            a.tag_category_id - b.tag_category_id
        )
        .map((s) => ({
          u: (s.uid ?? "").trim(),
          tid: s.text_id ?? 0,
          def: s.tag_definition_id,
          cat: s.tag_category_id,
          a: s.start_offset,
          b: s.end_offset,
        })),
    }))
  );
}

/**
 * Drops editor rows that cannot belong to `scene` (e.g. merged-in Yjs rows from another scene).
 * Keeps: client temp ids (`paragraph_id <= 0`), ids listed on the draft scene, or uids listed on the draft scene.
 */
export function filterParagraphsToDraftSceneMembership(
  paragraphs: DraftParagraph[],
  scene: DraftScene
): DraftParagraph[] {
  const allowedIds = new Set(scene.paragraphs.map((p) => p.paragraph_id));
  const allowedUids = new Set(
    scene.paragraphs
      .map((p) => p.paragraph_uid?.trim())
      .filter((u): u is string => Boolean(u && u.length > 0))
  );
  const filtered = paragraphs.filter((row) => {
    if (row.paragraph_id <= 0) return true;
    if (allowedIds.has(row.paragraph_id)) return true;
    const u = row.paragraph_uid?.trim();
    return Boolean(u && allowedUids.has(u));
  });
  return filtered.map((row, index) => ({ ...row, sort_order: index }));
}

/** Used for on-screen pagination and schedule page-length estimates. */
export const SCREENPLAY_LINES_PER_PAGE = 55;

const SCREENPLAY_TYPE_LINE_WIDTH: Record<ScreenplayParagraphType, number> = {
  scene_heading: 58,
  action: 62,
  character: 24,
  dialogue: 34,
  parenthetical: 28,
  shot: 58,
  transition: 24,
  general: 62,
};

const SCREENPLAY_TYPE_TOP_LINES: Record<ScreenplayParagraphType, number> = {
  scene_heading: 2,
  action: 1,
  character: 1,
  dialogue: 0,
  parenthetical: 0,
  shot: 1,
  transition: 1,
  general: 1,
};

export type ScreenplayPage = {
  pageNumber: number;
  paragraphs: DraftParagraph[];
  estimatedLines: number;
  startsWithContinuation: boolean;
  endsWithContinuation: boolean;
};

export function getNextParagraphType(
  currentType: ScreenplayParagraphType
): ScreenplayParagraphType {
  switch (currentType) {
    case "scene_heading":
      return "action";
    case "character":
      return "dialogue";
    case "parenthetical":
      return "dialogue";
    case "dialogue":
      return "action";
    case "transition":
      return "scene_heading";
    default:
      return currentType;
  }
}

/**
 * After deleting the half-open range `[delStart, delEnd)` from the paragraph text, remap tag
 * offsets into the new string. Drops spans fully inside the deleted range.
 */
export function remapTagSpansAfterRangeDelete(
  spans: DraftTagSpan[],
  delStart: number,
  delEnd: number
): DraftTagSpan[] {
  const delLen = delEnd - delStart;
  if (delLen <= 0) return spans.map((span) => ({ ...span }));

  const result: DraftTagSpan[] = [];
  for (const span of spans) {
    const s = span.start_offset;
    const e = span.end_offset;

    if (e <= delStart) {
      result.push({ ...span, start_offset: s, end_offset: e });
    } else if (s >= delEnd) {
      result.push({ ...span, start_offset: s - delLen, end_offset: e - delLen });
    } else if (s >= delStart && e <= delEnd) {
      // span fully removed
    } else if (s < delStart && e > delEnd) {
      result.push({ ...span, start_offset: s, end_offset: e - delLen });
    } else if (s < delStart && e > delStart && e <= delEnd) {
      const ne = delStart;
      if (s < ne) result.push({ ...span, start_offset: s, end_offset: ne });
    } else if (s >= delStart && s < delEnd && e > delEnd) {
      const ns = delStart;
      const ne = e - delLen;
      if (ns < ne) result.push({ ...span, start_offset: ns, end_offset: ne });
    }
  }
  return result.filter((span) => span.start_offset < span.end_offset);
}

/**
 * Map a cursor index in `prevText` (0…prevLen) to the corresponding index in `nextText` using a
 * character diff. Supports multiple disjoint edits (e.g. document find/replace within one paragraph).
 */
function mapOldCursorToNew(
  changes: Change[],
  oldCursor: number,
  oldLen: number,
  nextLen: number
): number {
  if (oldCursor <= 0) return 0;
  if (oldCursor >= oldLen) return nextLen;
  const c = oldCursor;
  let o = 0;
  let n = 0;
  for (const part of changes) {
    const len = part.value.length;
    if (part.removed && !part.added) {
      if (c < o + len) return n;
      o += len;
    } else if (part.added && !part.removed) {
      n += len;
    } else {
      if (c < o + len) return n + (c - o);
      o += len;
      n += len;
    }
  }
  return n;
}

function tagSpansFullyInsideText(spans: DraftTagSpan[], textLen: number): boolean {
  return spans.every(
    (span) =>
      span.start_offset >= 0 && span.start_offset < span.end_offset && span.end_offset <= textLen
  );
}

/** Clamp span offsets to `[0, textLen]` (matches editor decoration clipping). */
function clampTagSpansToTextLength(spans: DraftTagSpan[], textLen: number): DraftTagSpan[] {
  return spans
    .map((span) => {
      const lo = Math.max(0, Math.min(span.start_offset, textLen));
      const hi = Math.max(lo, Math.min(span.end_offset, textLen));
      return { ...span, start_offset: lo, end_offset: hi };
    })
    .filter((span) => span.start_offset < span.end_offset);
}

/**
 * Prefer TanStack `scene.paragraphs` tag geometry (same source as Applied Tags) while keeping each
 * block’s current `text` from editor state — remaps offsets when the body differs slightly.
 * Falls back to matching by `sort_order` + exact `text` when Yjs rows use ephemeral uids or temp ids.
 */
export function mergeSceneTagSpansOntoParagraphsForHighlight(
  localRows: DraftParagraph[],
  sceneRows: DraftParagraph[] | undefined
): DraftParagraph[] {
  if (!sceneRows?.length) return localRows;
  if (!localRows.length) {
    return sceneRows.map((row) => ({ ...row }));
  }
  const serverByUid = new Map<string, DraftParagraph>();
  const serverById = new Map<number, DraftParagraph>();
  for (const r of sceneRows) {
    const u = r.paragraph_uid?.trim();
    if (u) serverByUid.set(u, r);
    if (r.paragraph_id > 0) serverById.set(r.paragraph_id, r);
  }
  return localRows.map((local) => {
    const u = local.paragraph_uid?.trim();
    let srv =
      (u ? serverByUid.get(u) : undefined) ??
      (local.paragraph_id > 0 ? serverById.get(local.paragraph_id) : undefined);

    if (!srv) {
      const lt = typeof local.text === "string" ? local.text : "";
      srv = sceneRows.find(
        (row) =>
          row.sort_order === local.sort_order &&
          (typeof row.text === "string" ? row.text : "") === lt
      );
    }

    if (!srv) return local;

    if (srv.tag_spans === undefined) return local;

    const serverSpans = Array.isArray(srv.tag_spans) ? srv.tag_spans : [];
    if (!serverSpans.length) {
      return { ...local, tag_spans: [] };
    }

    const prev = typeof srv.text === "string" ? srv.text : "";
    const next = typeof local.text === "string" ? local.text : "";
    const nextLen = next.length;
    const localSpans = Array.isArray(local.tag_spans) ? local.tag_spans : [];

    if (prev !== next && localSpans.length > 0 && tagSpansFullyInsideText(localSpans, nextLen)) {
      return {
        ...local,
        tag_spans: mergeAdjacentTagSpans(localSpans.map((span) => ({ ...span }))),
      };
    }

    let tag_spans: DraftTagSpan[];
    if (prev === next) {
      const clampedServer = clampTagSpansToTextLength(serverSpans, nextLen);
      if (localSpans.length > 0 && tagSpansFullyInsideText(localSpans, nextLen)) {
        tag_spans = envelopeTagSpansByTouchingClusters(
          [...localSpans.map((span) => ({ ...span })), ...clampedServer],
          nextLen
        );
      } else {
        tag_spans = envelopeTagSpansByTouchingClusters(clampedServer, nextLen);
      }
    } else {
      tag_spans = clampTagSpansToTextLength(
        remapTagSpansAfterTextEdit(serverSpans, prev, next),
        nextLen
      );
    }
    return { ...local, tag_spans: mergeAdjacentTagSpans(tag_spans) };
  });
}

/**
 * Remap tag spans after paragraph `text` changed from `prevText` to `nextText`.
 * Uses a character diff so multiple replacements in the same block (e.g. find/replace all) remap
 * correctly. Drops spans that collapse to empty ranges.
 */
export function remapTagSpansAfterTextEdit(
  spans: DraftTagSpan[],
  prevText: string,
  nextText: string
): DraftTagSpan[] {
  if (prevText === nextText) return spans.map((span) => ({ ...span }));
  const changes = diffChars(prevText, nextText);
  if (!changes.length) return spans.map((span) => ({ ...span }));

  const oldLen = prevText.length;
  const nextLen = nextText.length;
  const result: DraftTagSpan[] = [];

  for (const span of spans) {
    const rawS = span.start_offset;
    const rawE = span.end_offset;
    if (rawE <= rawS) continue;

    const s = Math.max(0, Math.min(rawS, oldLen));
    const e = Math.max(s, Math.min(rawE, oldLen));

    let ns = mapOldCursorToNew(changes, s, oldLen, nextLen);
    let ne = mapOldCursorToNew(changes, e, oldLen, nextLen);
    ns = Math.max(0, Math.min(ns, nextLen));
    ne = Math.max(ns, Math.min(ne, nextLen));

    if (ns < ne) {
      result.push({ ...span, start_offset: ns, end_offset: ne });
    }
  }

  return mergeAdjacentTagSpans(result);
}

/**
 * Merge touching spans that reference the same tag definition (avoids split highlights when Yjs /
 * find-replace left adjacent fragments with different text block ids for the same on-screen tag).
 */
export function mergeAdjacentTagSpans(spans: DraftTagSpan[]): DraftTagSpan[] {
  if (spans.length < 2) return spans.map((span) => ({ ...span }));
  const sorted = [...spans].sort(
    (a, b) => a.start_offset - b.start_offset || a.end_offset - b.end_offset
  );
  const out: DraftTagSpan[] = [];
  for (const span of sorted) {
    const prev = out[out.length - 1];
    if (
      prev &&
      prev.end_offset === span.start_offset &&
      prev.tag_definition_id === span.tag_definition_id
    ) {
      const prevTid = typeof prev.text_id === "number" && prev.text_id > 0 ? prev.text_id : 0;
      const spanTid = typeof span.text_id === "number" && span.text_id > 0 ? span.text_id : 0;
      const text_id = prevTid > 0 ? prevTid : spanTid;
      const prevCat = Number(prev.tag_category_id);
      const spanCat = Number(span.tag_category_id);
      const tag_category_id =
        prevCat === spanCat ? prevCat : prevCat > 0 ? prevCat : spanCat > 0 ? spanCat : prevCat;
      const pUid = (prev.uid ?? "").trim();
      out[out.length - 1] = {
        ...prev,
        end_offset: span.end_offset,
        text_id,
        tag_category_id,
        uid: pUid.length > 0 ? prev.uid : span.uid,
        label: prev.label ?? span.label ?? "",
        tag_display_name: prev.tag_display_name ?? span.tag_display_name ?? "",
        tag_merged_label: prev.tag_merged_label ?? span.tag_merged_label ?? null,
      };
    } else {
      out.push({ ...span });
    }
  }
  return out;
}

/**
 * Builds one span per cluster of overlapping/touching ranges with the same `tag_definition_id`,
 * clipping to `[0, textLen]`.
 */
export function envelopeTagSpansByTouchingClusters(
  spans: DraftTagSpan[],
  textLen: number
): DraftTagSpan[] {
  const trimmed = spans
    .map((span) => {
      const lo = Math.max(0, Math.min(span.start_offset, textLen));
      const hi = Math.max(lo, Math.min(span.end_offset, textLen));
      return { ...span, start_offset: lo, end_offset: hi };
    })
    .filter((span) => span.start_offset < span.end_offset);
  if (!trimmed.length) return [];

  const sorted = [...trimmed].sort(
    (a, b) =>
      a.tag_definition_id - b.tag_definition_id ||
      a.start_offset - b.start_offset ||
      a.end_offset - b.end_offset
  );

  const clusters: DraftTagSpan[][] = [];
  for (const s of sorted) {
    const lastCluster = clusters[clusters.length - 1];
    const lastSpan = lastCluster?.[lastCluster.length - 1];
    const sameDef = Boolean(lastSpan && lastSpan.tag_definition_id === s.tag_definition_id);
    const overlapsOrTouches = Boolean(lastSpan && s.start_offset <= lastSpan.end_offset);
    if (sameDef && overlapsOrTouches) {
      lastCluster!.push(s);
    } else {
      clusters.push([s]);
    }
  }

  const widened: DraftTagSpan[] = [];
  for (const c of clusters) {
    const first = c[0]!;
    let lo = first.start_offset;
    let hi = first.end_offset;
    for (let i = 1; i < c.length; i++) {
      const s = c[i]!;
      lo = Math.min(lo, s.start_offset);
      hi = Math.max(hi, s.end_offset);
    }
    widened.push({ ...first, start_offset: lo, end_offset: hi });
  }
  return mergeAdjacentTagSpans(widened);
}

/**
 * Matches the previous React draft row before widen merges PM tag spans. Collaboration can leave
 * TipTap JSON with different uids/temp ids than the last `paragraphsRef` snapshot; use index +
 * `sort_order` + `text` only when we can still prove it is the same block and we are salvaging spans.
 */
export function resolveDraftParagraphPriorForEnvelopeWiden(
  row: DraftParagraph,
  prevRows: DraftParagraph[],
  index: number,
  prevById: Map<number, DraftParagraph>,
  prevByUid: Map<string, DraftParagraph>
): DraftParagraph | undefined {
  const trimmedUid = row.paragraph_uid?.trim() ?? "";
  let pri =
    (row.paragraph_id > 0 ? prevById.get(row.paragraph_id) : undefined) ??
    (trimmedUid.length > 0 ? prevByUid.get(trimmedUid) : undefined);

  if (pri != null) return pri;

  const slot = prevRows[index];
  if (
    slot == null ||
    slot.sort_order !== row.sort_order ||
    slot.text !== row.text ||
    (slot.tag_spans?.length ?? 0) === 0
  ) {
    return undefined;
  }
  if (row.paragraph_id > 0 && slot.paragraph_id > 0 && row.paragraph_id !== slot.paragraph_id) {
    return undefined;
  }
  return slot;
}

/**
 * Merges PM-derived tag spans with the prior React paragraph row when `text` is unchanged.
 * Finds clusters of spans with the same `tag_definition_id` whose ranges touch or overlap and
 * replaces them with a single envelope span so a split mark (e.g. after find/replace) does not
 * shrink highlights in React state on the next `onUpdate`.
 */
export function widenTagSpansUsingParagraphEnvelope(
  prior: DraftParagraph | undefined,
  fromPm: DraftParagraph
): DraftTagSpan[] {
  const text = typeof fromPm.text === "string" ? fromPm.text : "";
  const textLen = text.length;
  const pmOnly = Array.isArray(fromPm.tag_spans) ? fromPm.tag_spans : [];
  if (!prior) {
    return envelopeTagSpansByTouchingClusters(pmOnly, textLen);
  }

  const priorSpans = Array.isArray(prior.tag_spans) ? prior.tag_spans : [];
  const priorText = typeof prior.text === "string" ? prior.text : "";

  /** When Yjs/PM commits before React `paragraphsRef` catches up, only PM spans would widen wrong. */
  const priorMapped =
    priorText === text
      ? priorSpans.map((span) => ({ ...span }))
      : remapTagSpansAfterTextEdit(priorSpans, priorText, text);

  if (priorMapped.length === 0 && pmOnly.length === 0) return [];

  const combined = [...priorMapped, ...pmOnly];
  return envelopeTagSpansByTouchingClusters(combined, textLen);
}

/** Split tag spans when paragraph text is cut at `splitAt` (second block starts at index 0). */
export function splitTagSpansAtOffset(
  spans: DraftTagSpan[],
  splitAt: number
): { beforeSpans: DraftTagSpan[]; afterSpans: DraftTagSpan[] } {
  const beforeSpans: DraftTagSpan[] = [];
  const afterSpans: DraftTagSpan[] = [];

  for (const span of spans) {
    const s = span.start_offset;
    const e = span.end_offset;

    if (e <= splitAt) {
      beforeSpans.push(span);
    } else if (s >= splitAt) {
      afterSpans.push({
        ...span,
        start_offset: s - splitAt,
        end_offset: e - splitAt,
      });
    } else {
      beforeSpans.push({ ...span, start_offset: s, end_offset: splitAt });
      afterSpans.push({
        ...span,
        start_offset: 0,
        end_offset: e - splitAt,
      });
    }
  }

  return {
    beforeSpans: beforeSpans.filter((span) => span.start_offset < span.end_offset),
    afterSpans: afterSpans.filter((span) => span.start_offset < span.end_offset),
  };
}

/**
 * End-of-block Enter advances element type (Final Draft–style). Mid-block Enter keeps the same
 * type so split dialogue stays dialogue.
 */
export function resolveStructuralParagraphType(
  currentType: ScreenplayParagraphType,
  mergedLength: number,
  splitAt: number,
  hadNonZeroSelection: boolean
): ScreenplayParagraphType {
  const atEnd = splitAt === mergedLength && !hadNonZeroSelection;
  if (atEnd) return getNextParagraphType(currentType);
  return currentType;
}

/**
 * Font weight, case, and alignment for the screenplay body. Shared by the textarea and
 * the tag mirror so highlights line up with glyphs.
 */
export function getParagraphBodyTypographyClassName(type: ScreenplayParagraphType): string {
  switch (type) {
    case "scene_heading":
      return "font-bold uppercase tracking-[0.01em]";
    case "character":
      return "text-center font-bold uppercase";
    case "dialogue":
      return "text-left";
    case "parenthetical":
      return "text-center italic";
    case "transition":
      return "text-right font-bold uppercase";
    case "shot":
      return "font-bold uppercase";
    default:
      return "";
  }
}

function getParagraphWidthLayoutClassName(
  type: ScreenplayParagraphType,
  widthInsideMirrorShell: boolean
): string {
  if (widthInsideMirrorShell) {
    return "";
  }
  switch (type) {
    case "character":
      return "mx-auto block w-full max-w-[min(100%,2.2in)]";
    case "dialogue":
      return "mx-auto block w-full max-w-[min(100%,3.35in)]";
    case "parenthetical":
      return "mx-auto block w-full max-w-[min(100%,2.55in)]";
    case "transition":
      return "ml-auto block w-full max-w-[min(100%,2in)]";
    default:
      return "";
  }
}

const PARAGRAPH_TEXTAREA_BASE =
  "w-full resize-none overflow-hidden border-0 bg-transparent p-0 m-0 box-border font-['Courier_New',Courier,monospace] text-[12pt] leading-[1.15] text-[rgb(var(--sp-ink))] outline-none focus:ring-0 appearance-none";

/**
 * @param options.widthInsideMirrorShell — when true, width is provided by
 * {@link getParagraphMirrorShellClassName}; omit nested max-width so the textarea and
 * mirror share one column without double constraints.
 */
export function getParagraphClassName(
  type: ScreenplayParagraphType,
  options?: { widthInsideMirrorShell?: boolean }
) {
  const widthInsideMirrorShell = options?.widthInsideMirrorShell ?? false;
  const width = getParagraphWidthLayoutClassName(type, widthInsideMirrorShell);
  const typography = getParagraphBodyTypographyClassName(type);
  return [PARAGRAPH_TEXTAREA_BASE, width, typography].filter(Boolean).join(" ");
}

/**
 * Shell around the tag mirror + textarea stack. Must match the width and horizontal
 * alignment implied by {@link getParagraphClassName} so the mirror layer and textarea
 * share the same line boxes (centered character, narrow dialogue column, etc.).
 */
export function getParagraphMirrorShellClassName(type: ScreenplayParagraphType): string {
  switch (type) {
    case "character":
      return "relative mx-auto w-full max-w-[min(100%,2.2in)]";
    case "dialogue":
      return "relative mx-auto w-full max-w-[min(100%,3.35in)]";
    case "parenthetical":
      return "relative mx-auto w-full max-w-[min(100%,2.55in)]";
    case "transition":
      return "relative ml-auto w-full max-w-[min(100%,2in)]";
    case "scene_heading":
    case "shot":
    default:
      return "relative w-full";
  }
}

export function getParagraphContainerClassName(type: ScreenplayParagraphType) {
  switch (type) {
    case "scene_heading":
      return "pt-[2em]";
    case "character":
      return "pt-[1em]";
    case "dialogue":
      return "pt-0";
    case "parenthetical":
      return "pt-0";
    case "transition":
      return "pt-[1em]";
    default:
      return "pt-[1em]";
  }
}

/**
 * Tailwind arbitrary variants under `.ProseMirror` so TipTap blocks match
 * {@link getParagraphClassName} width, spacing, and typography.
 *
 * **Important:** Each utility must appear literally in source so Tailwind JIT emits it.
 * Do not build these strings by concatenating `data-type` — unused classes are stripped.
 */
export const SCREENPLAY_TIPTAP_PROSE_MIRROR_PARAGRAPH_CLASSES = [
  // scene_heading — matches getParagraphContainerClassName + typography + full-width shell
  "[&_p[data-screenplay='paragraph'][data-type='scene_heading']]:pt-[2em]",
  "[&_p[data-screenplay='paragraph'][data-type='scene_heading']]:w-full",
  "[&_p[data-screenplay='paragraph'][data-type='scene_heading']]:text-left",
  "[&_p[data-screenplay='paragraph'][data-type='scene_heading']]:font-bold",
  "[&_p[data-screenplay='paragraph'][data-type='scene_heading']]:uppercase",
  "[&_p[data-screenplay='paragraph'][data-type='scene_heading']]:tracking-[0.01em]",
  // action
  "[&_p[data-screenplay='paragraph'][data-type='action']]:pt-[1em]",
  "[&_p[data-screenplay='paragraph'][data-type='action']]:w-full",
  "[&_p[data-screenplay='paragraph'][data-type='action']]:text-left",
  // character
  "[&_p[data-screenplay='paragraph'][data-type='character']]:pt-[1em]",
  "[&_p[data-screenplay='paragraph'][data-type='character']]:mx-auto",
  "[&_p[data-screenplay='paragraph'][data-type='character']]:block",
  "[&_p[data-screenplay='paragraph'][data-type='character']]:w-full",
  "[&_p[data-screenplay='paragraph'][data-type='character']]:max-w-[min(100%,2.2in)]",
  "[&_p[data-screenplay='paragraph'][data-type='character']]:text-center",
  "[&_p[data-screenplay='paragraph'][data-type='character']]:font-bold",
  "[&_p[data-screenplay='paragraph'][data-type='character']]:uppercase",
  // dialogue
  "[&_p[data-screenplay='paragraph'][data-type='dialogue']]:pt-0",
  "[&_p[data-screenplay='paragraph'][data-type='dialogue']]:mx-auto",
  "[&_p[data-screenplay='paragraph'][data-type='dialogue']]:block",
  "[&_p[data-screenplay='paragraph'][data-type='dialogue']]:w-full",
  "[&_p[data-screenplay='paragraph'][data-type='dialogue']]:max-w-[min(100%,3.35in)]",
  "[&_p[data-screenplay='paragraph'][data-type='dialogue']]:text-left",
  // parenthetical
  "[&_p[data-screenplay='paragraph'][data-type='parenthetical']]:pt-0",
  "[&_p[data-screenplay='paragraph'][data-type='parenthetical']]:mx-auto",
  "[&_p[data-screenplay='paragraph'][data-type='parenthetical']]:block",
  "[&_p[data-screenplay='paragraph'][data-type='parenthetical']]:w-full",
  "[&_p[data-screenplay='paragraph'][data-type='parenthetical']]:max-w-[min(100%,2.55in)]",
  "[&_p[data-screenplay='paragraph'][data-type='parenthetical']]:text-center",
  "[&_p[data-screenplay='paragraph'][data-type='parenthetical']]:italic",
  // shot
  "[&_p[data-screenplay='paragraph'][data-type='shot']]:pt-[1em]",
  "[&_p[data-screenplay='paragraph'][data-type='shot']]:w-full",
  "[&_p[data-screenplay='paragraph'][data-type='shot']]:text-left",
  "[&_p[data-screenplay='paragraph'][data-type='shot']]:font-bold",
  "[&_p[data-screenplay='paragraph'][data-type='shot']]:uppercase",
  // transition
  "[&_p[data-screenplay='paragraph'][data-type='transition']]:pt-[1em]",
  "[&_p[data-screenplay='paragraph'][data-type='transition']]:ml-auto",
  "[&_p[data-screenplay='paragraph'][data-type='transition']]:block",
  "[&_p[data-screenplay='paragraph'][data-type='transition']]:w-full",
  "[&_p[data-screenplay='paragraph'][data-type='transition']]:max-w-[min(100%,2in)]",
  "[&_p[data-screenplay='paragraph'][data-type='transition']]:text-right",
  "[&_p[data-screenplay='paragraph'][data-type='transition']]:font-bold",
  "[&_p[data-screenplay='paragraph'][data-type='transition']]:uppercase",
  // general
  "[&_p[data-screenplay='paragraph'][data-type='general']]:pt-[1em]",
  "[&_p[data-screenplay='paragraph'][data-type='general']]:w-full",
  "[&_p[data-screenplay='paragraph'][data-type='general']]:text-left",
].join(" ");

export function normalizeParagraphForSave(paragraph: DraftParagraph, index: number) {
  const uid = paragraph.paragraph_uid?.trim();
  const text =
    paragraph.type === "scene_heading" && typeof paragraph.source_text_preserved !== "boolean"
      ? paragraph.text.replace(/\r\n/g, "\n").trimEnd().toUpperCase()
      : paragraph.text;
  return {
    paragraph_id: paragraph.paragraph_id > 0 ? paragraph.paragraph_id : undefined,
    ...(uid && uid.length > 0 ? { paragraph_uid: uid } : {}),
    type: paragraph.type,
    sort_order: index,
    text,
  };
}

export function getSelectionOccurrence(
  fullText: string,
  selectedText: string,
  selectionStart: number
) {
  const safeText = selectedText.trim();
  if (!safeText) return 0;

  let matchCount = 0;
  let searchIndex = 0;

  while (searchIndex <= selectionStart) {
    const foundIndex = fullText.indexOf(safeText, searchIndex);
    if (foundIndex === -1 || foundIndex > selectionStart) break;
    matchCount += 1;
    searchIndex = foundIndex + safeText.length;
  }

  // 0-based index for POST .../tag (CreateTagDto.occurrence) and splitByOccurrence
  return Math.max(matchCount - 1, 0);
}

/** Whole-block comment anchor for controls that do not have a textarea (e.g. TipTap gutter). */
export function buildParagraphWholeCommentTarget(paragraph: DraftParagraph): TagSelection | null {
  if (!paragraph.text_id && !paragraph.paragraph_uid) {
    return null;
  }
  const preview = paragraph.text.trim().slice(0, 200) || "(Empty block)";
  return {
    paragraphId: paragraph.paragraph_id,
    paragraphUid: paragraph.paragraph_uid,
    textId: paragraph.text_id,
    selectedText: preview,
    startOffset: 0,
    endOffset: 0,
    occurrence: 0,
    anchorMode: "paragraph",
  };
}

/**
 * Builds a comment composer target from the current textarea selection, or a whole-paragraph
 * anchor when the selection is collapsed (Google Docs–style margin comment).
 */
export function buildScriptCommentComposerTarget(
  paragraph: DraftParagraph,
  textarea: HTMLTextAreaElement
): TagSelection | null {
  if (!paragraph.text_id && !paragraph.paragraph_uid) {
    return null;
  }

  const rawSelection = textarea.value.slice(textarea.selectionStart, textarea.selectionEnd);
  const leadingWhitespace = rawSelection.length - rawSelection.trimStart().length;
  const trimmedSelection = rawSelection.trim();

  if (trimmedSelection) {
    const startOffset = textarea.selectionStart + leadingWhitespace;
    return {
      paragraphId: paragraph.paragraph_id,
      paragraphUid: paragraph.paragraph_uid,
      textId: paragraph.text_id,
      selectedText: trimmedSelection,
      startOffset,
      endOffset: startOffset + trimmedSelection.length,
      occurrence: getSelectionOccurrence(textarea.value, trimmedSelection, textarea.selectionStart),
    };
  }

  const preview = paragraph.text.trim().slice(0, 200) || "(Empty block)";
  return {
    paragraphId: paragraph.paragraph_id,
    paragraphUid: paragraph.paragraph_uid,
    textId: paragraph.text_id,
    selectedText: preview,
    startOffset: 0,
    endOffset: 0,
    occurrence: 0,
    anchorMode: "paragraph",
  };
}

/**
 * Hit-test which screenplay block is under the pointer (legacy `[data-paragraph-id]` wrappers or TipTap
 * `p[data-screenplay]` with `data-paragraph-id` / `data-uid`).
 */
export function resolveParagraphIdUnderPoint(
  clientX: number,
  clientY: number,
  root: HTMLElement,
  paragraphs: DraftParagraph[]
): number | null {
  const walkFromHitTarget = (): number | null => {
    const top = document.elementFromPoint(clientX, clientY);
    let node: Element | null = top;
    while (node && node !== root) {
      if (node instanceof HTMLElement) {
        const pid = node.dataset.paragraphId;
        if (pid) {
          const id = Number(pid);
          if (!Number.isNaN(id)) return id;
        }
        const uid = node.dataset.uid?.trim();
        if (uid) {
          const row = paragraphs.find((p) => p.paragraph_uid?.trim() === uid);
          if (row) return row.paragraph_id;
        }
      }
      node = node.parentElement;
    }
    return null;
  };

  const fromDom = walkFromHitTarget();
  if (fromDom != null) return fromDom;

  const papers = root.querySelectorAll<HTMLElement>(".script-screenplay-paper");
  let paper: HTMLElement | null = null;
  for (const candidate of papers) {
    const rect = candidate.getBoundingClientRect();
    if (
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    ) {
      paper = candidate;
      break;
    }
  }
  if (!paper) return null;

  const blocks = paper.querySelectorAll<HTMLElement>("[data-paragraph-id], [data-uid]");
  let bestId: number | null = null;
  let bestDist = Infinity;
  let bestTop = Infinity;

  for (const el of blocks) {
    const rect = el.getBoundingClientRect();
    const idAttr = el.dataset.paragraphId;
    const uidAttr = el.dataset.uid?.trim();
    let id: number | null = null;
    if (idAttr) {
      const n = Number(idAttr);
      if (!Number.isNaN(n)) id = n;
    }
    if (id == null && uidAttr) {
      const row = paragraphs.find((p) => p.paragraph_uid?.trim() === uidAttr);
      id = row?.paragraph_id ?? null;
    }
    if (id == null) continue;

    const verticalDist =
      clientY < rect.top ? rect.top - clientY : clientY > rect.bottom ? clientY - rect.bottom : 0;

    if (verticalDist < bestDist || (verticalDist === bestDist && rect.top < bestTop)) {
      bestDist = verticalDist;
      bestId = id;
      bestTop = rect.top;
    }
  }

  return bestId;
}

export function paginateScreenplayParagraphs(paragraphs: DraftParagraph[]): ScreenplayPage[] {
  if (paragraphs.length === 0) {
    return [
      {
        pageNumber: 1,
        paragraphs: [],
        estimatedLines: 0,
        startsWithContinuation: false,
        endsWithContinuation: false,
      },
    ];
  }

  const pages: ScreenplayPage[] = [];
  let currentPage: DraftParagraph[] = [];
  let currentLineCount = 0;
  const groups = groupScreenplayParagraphs(paragraphs);

  for (const group of groups) {
    const groupLineCount = group.reduce(
      (count, paragraph) => count + estimateParagraphLineCount(paragraph),
      0
    );
    const shouldStartNewPage =
      currentPage.length > 0 && currentLineCount + groupLineCount > SCREENPLAY_LINES_PER_PAGE;

    if (shouldStartNewPage) {
      pages.push(toScreenplayPage(pages.length + 1, currentPage, currentLineCount));
      currentPage = [];
      currentLineCount = 0;
    }

    currentPage.push(...group);
    currentLineCount += groupLineCount;
  }

  pages.push(toScreenplayPage(pages.length + 1, currentPage, currentLineCount));

  return pages;
}

export function estimateScreenplayPageCount(paragraphs: DraftParagraph[]) {
  return paginateScreenplayParagraphs(paragraphs).length;
}

export function estimateParagraphLineCount(paragraph: DraftParagraph) {
  const lineWidth =
    SCREENPLAY_TYPE_LINE_WIDTH[paragraph.type] ?? SCREENPLAY_TYPE_LINE_WIDTH.general;
  const topLines = SCREENPLAY_TYPE_TOP_LINES[paragraph.type] ?? SCREENPLAY_TYPE_TOP_LINES.general;
  const textLines = paragraph.text
    .split("\n")
    .reduce(
      (count, line) => count + Math.max(1, Math.ceil(Math.max(line.length, 1) / lineWidth)),
      0
    );

  return topLines + textLines;
}

/** Schedule "page length" string from the same line estimate as on-screen page breaks. */
export function formatSchedulePageLengthFromParagraphs(paragraphs: DraftParagraph[]): string {
  const totalLines = paragraphs.reduce((sum, p) => sum + estimateParagraphLineCount(p), 0);
  return formatScheduleLengthFromLineTotals(totalLines, SCREENPLAY_LINES_PER_PAGE);
}

function groupScreenplayParagraphs(paragraphs: DraftParagraph[]) {
  const groups: DraftParagraph[][] = [];
  let index = 0;

  while (index < paragraphs.length) {
    const paragraph = paragraphs[index];

    if (paragraph.type === "character") {
      const dialogueGroup = [paragraph];
      let nextIndex = index + 1;

      while (
        nextIndex < paragraphs.length &&
        ["parenthetical", "dialogue"].includes(paragraphs[nextIndex].type)
      ) {
        dialogueGroup.push(paragraphs[nextIndex]);
        nextIndex += 1;
      }

      groups.push(dialogueGroup);
      index = nextIndex;
      continue;
    }

    groups.push([paragraph]);
    index += 1;
  }

  return groups;
}

function toScreenplayPage(
  pageNumber: number,
  paragraphs: DraftParagraph[],
  estimatedLines: number
): ScreenplayPage {
  return {
    pageNumber,
    paragraphs,
    estimatedLines,
    startsWithContinuation: startsWithContinuation(paragraphs),
    endsWithContinuation: endsWithContinuation(paragraphs),
  };
}

function startsWithContinuation(paragraphs: DraftParagraph[]) {
  const firstParagraph = paragraphs[0];
  return firstParagraph?.type === "dialogue" || firstParagraph?.type === "parenthetical";
}

function endsWithContinuation(paragraphs: DraftParagraph[]) {
  const lastParagraph = paragraphs.at(-1);
  return lastParagraph?.type === "character" || lastParagraph?.type === "parenthetical";
}

const TRIPLE_DOTS_PATTERN = /\.\.\./g;
const DOUBLE_HYPHEN_PATTERN = /(\w)\s?--\s?(\w)/g;
const STRAIGHT_DOUBLE_QUOTE_PATTERN = /"/g;
const STRAIGHT_SINGLE_QUOTE_PATTERN = /'/g;

export function applySmartTypography(input: string): string {
  if (!input) return input;
  let output = input;
  output = output.replace(TRIPLE_DOTS_PATTERN, "\u2026");
  output = output.replace(DOUBLE_HYPHEN_PATTERN, "$1\u2014$2");

  let openDouble = true;
  output = output.replace(STRAIGHT_DOUBLE_QUOTE_PATTERN, () => {
    const replacement = openDouble ? "\u201C" : "\u201D";
    openDouble = !openDouble;
    return replacement;
  });

  output = output.replace(
    STRAIGHT_SINGLE_QUOTE_PATTERN,
    (match, offset: number, source: string) => {
      const previousChar = source[offset - 1] ?? "";
      if (/\w/.test(previousChar)) {
        return "\u2019";
      }
      return /\s|^/.test(previousChar) ? "\u2018" : "\u2019";
    }
  );

  return output;
}

export function applySmartTypographyToParagraphs<
  T extends { text: string; source_text_preserved?: boolean },
>(paragraphs: T[]): T[] {
  return paragraphs.map((paragraph) => {
    if (typeof paragraph.source_text_preserved === "boolean") return paragraph;
    const next = applySmartTypography(paragraph.text);
    return next === paragraph.text ? paragraph : { ...paragraph, text: next };
  });
}

const WORD_PATTERN = /[\p{L}\p{N}'\u2019]+/gu;

export function countWords(text: string): number {
  if (!text) return 0;
  const matches = text.match(WORD_PATTERN);
  return matches ? matches.length : 0;
}

export function countParagraphsWords(paragraphs: { text: string }[]): number {
  return paragraphs.reduce((total, paragraph) => total + countWords(paragraph.text), 0);
}

export function formatRuntimeEstimateMinutes(pageCount: number): string {
  const minutes = Math.max(0, Math.round(pageCount));
  if (minutes < 1) return "<1 min";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder === 0 ? `${hours} hr` : `${hours} hr ${remainder} min`;
}

/** Title-case tag category name for display (matches breakdown UI). */
export function formatTagCategoryDisplayName(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * One row per canonical name: script-specific categories override platform defaults.
 * When two document-scoped rows share a name, the higher tag_category_id wins (newer).
 */
export function dedupeTagCategoriesByCanonicalName(categories: TagCategory[]): TagCategory[] {
  const byName = new Map<string, TagCategory>();
  for (const c of categories) {
    const key = c.name.trim().toLowerCase();
    const prev = byName.get(key);
    if (!prev) {
      byName.set(key, c);
      continue;
    }
    const prevIsDoc = prev.document_id != null;
    const nextIsDoc = c.document_id != null;
    if (!prevIsDoc && nextIsDoc) {
      byName.set(key, c);
    } else if (prevIsDoc && nextIsDoc) {
      byName.set(key, c.tag_category_id >= prev.tag_category_id ? c : prev);
    }
  }
  return Array.from(byName.values()).sort((a, b) =>
    formatTagCategoryDisplayName(a.name).localeCompare(formatTagCategoryDisplayName(b.name))
  );
}

export function filterTagCategoriesByQuery(
  deduped: TagCategory[],
  queryRaw: string
): TagCategory[] {
  const q = queryRaw.trim().toLowerCase();
  if (!q) return deduped;
  return deduped.filter((c) => {
    const label = formatTagCategoryDisplayName(c.name).toLowerCase();
    const raw = c.name.toLowerCase();
    return label.includes(q) || raw.includes(q);
  });
}

/** Maps every raw tag_category_id to the canonical row used in UI (deduped by name). */
export function buildCanonicalTagCategoryByIdMap(
  categories: TagCategory[]
): Map<number, TagCategory> {
  const deduped = dedupeTagCategoriesByCanonicalName(categories);
  const canonicalByKey = new Map(deduped.map((c) => [c.name.trim().toLowerCase(), c]));
  const resolved = new Map<number, TagCategory>();
  for (const c of categories) {
    const key = c.name.trim().toLowerCase();
    resolved.set(c.tag_category_id, canonicalByKey.get(key) ?? c);
  }
  return resolved;
}

export type SceneScheduleDraftFields = {
  scene_number: string;
  page: string;
  length: string;
  /** When false, `length` is derived from script line estimate until the user picks a custom value. */
  length_is_manual: boolean;
  /** INT./EXT./… — mirrors scene heading breakdown (Final Draft / StudioBinder). */
  int_ext: string;
  location: string;
  time: string;
  summary: string;
};

/**
 * Canonicalize INT/EXT prefixes so slug panel edits compose the same heading line as
 * stored screenplay headings (`INT.` / `EXT.`), not bare `INT` / `EXT`.
 */
export function normalizeIntExtForSlug(intExt: string): string {
  const raw = intExt.trim().toUpperCase().replace(/\s+/g, "");
  if (raw === "" || raw === "—") return "";
  if (raw === "INT" || raw === "INT.") return "INT.";
  if (raw === "EXT" || raw === "EXT.") return "EXT.";
  if (raw === "EST" || raw === "EST.") return "EST.";
  if (
    raw === "INT./EXT." ||
    raw === "INT/EXT." ||
    raw === "INT/EXT" ||
    raw === "EXT./INT." ||
    raw === "EXT/INT." ||
    raw === "EXT/INT" ||
    raw === "I/E." ||
    raw === "I/E"
  ) {
    return raw === "EXT./INT." || raw === "EXT/INT." || raw === "EXT/INT"
      ? "EXT./INT."
      : "INT./EXT.";
  }
  return intExt.trim().toUpperCase();
}

/** Parsed slug segments from a canonical scene heading line (uppercase storage). */
export type ParsedSceneHeading = {
  int_ext?: string;
  location?: string;
  time?: string;
};

/**
 * Mirrors backend `parseSceneHeading` — keep in sync with screenplay-editing slug breakdown.
 */
export function parseSceneHeading(sceneHeading: string): ParsedSceneHeading {
  const trimmed = sceneHeading.trim();
  const prefixMatch = trimmed.match(
    /^(INT\.\/EXT\.|EXT\.\/INT\.|INT\/EXT\.|EXT\/INT\.|I\/E\.|INT\.|EXT\.|EST\.|INT|EXT)\s+/i
  );
  if (!prefixMatch) {
    return {};
  }

  const intExt = prefixMatch[1]!.toUpperCase();
  const remainder = trimmed.slice(prefixMatch[0]!.length);

  const noLocationMatch = remainder.match(/^\s*-\s+(.+)$/);
  if (noLocationMatch) {
    return {
      int_ext: intExt,
      location: "",
      time: noLocationMatch[1]!.trim(),
    };
  }

  if (remainder.trim() === "") {
    return { int_ext: intExt };
  }

  const tailMatch = remainder.match(/^(.+?)(?:\s+-\s+(.+))?$/);
  if (!tailMatch) {
    return { int_ext: intExt };
  }

  return {
    int_ext: intExt,
    location: tailMatch[1]!.trim(),
    time: tailMatch[2]?.trim(),
  };
}

/**
 * When stored slug columns are empty but `scene_heading` parses, fill gaps for the schedule panel.
 * Does not replace non-empty DB values. Mirrors backend `enrichSlugFieldsFromSceneHeading`.
 */
export function enrichSlugFieldsForScheduleDraft<
  T extends {
    scene_heading?: string | null;
    int_ext?: string | null;
    location?: string | null;
    time?: string | null;
  },
>(scene: T): T {
  const parsed = parseSceneHeading(scene.scene_heading ?? "");
  if (!parsed.int_ext && !parsed.location && !parsed.time) {
    return scene;
  }

  const hasIntExt = scene.int_ext != null && String(scene.int_ext).trim() !== "";
  const hasLocation = scene.location != null && String(scene.location).trim() !== "";
  const hasTime = scene.time != null && String(scene.time).trim() !== "";

  const parsedTimeNorm = (parsed.time ?? "").trim().toUpperCase();
  const storedLocNorm = String(scene.location ?? "")
    .trim()
    .toUpperCase();
  const storedTimeNorm = String(scene.time ?? "")
    .trim()
    .toUpperCase();
  const parsedLocNorm = (parsed.location ?? "").trim();

  const redundantLocationFromOldParse =
    parsedTimeNorm.length > 0 &&
    parsedLocNorm === "" &&
    (storedLocNorm === `-${parsedTimeNorm}` || storedLocNorm === `- ${parsedTimeNorm}`) &&
    (!storedTimeNorm || storedTimeNorm === parsedTimeNorm);

  return {
    ...scene,
    int_ext: hasIntExt ? scene.int_ext : (parsed.int_ext ?? scene.int_ext),
    location: redundantLocationFromOldParse
      ? ""
      : hasLocation
        ? scene.location
        : (parsed.location ?? scene.location),
    time: hasTime ? scene.time : (parsed.time ?? scene.time),
  };
}

export function sceneScheduleDraftFromScene(scene: {
  scene_number?: string | null;
  scene_heading?: string | null;
  page?: number | null;
  length?: string | null;
  length_is_manual?: boolean | null;
  int_ext?: string | null;
  location?: string | null;
  time?: string | null;
  summary?: string | null;
}): SceneScheduleDraftFields {
  const enriched = enrichSlugFieldsForScheduleDraft(scene);
  return {
    scene_number: enriched.scene_number ?? "",
    page: enriched.page != null ? String(enriched.page) : "",
    length: enriched.length ?? "",
    length_is_manual: enriched.length_is_manual ?? false,
    int_ext: normalizeIntExtForSlug(enriched.int_ext ?? ""),
    location: (enriched.location ?? "").trim().toUpperCase(),
    time: (enriched.time ?? "").trim().toUpperCase(),
    summary: enriched.summary ?? "",
  };
}

/**
 * Builds the scene heading line from schedule slug fields. Spacing and uppercase match
 * {@link normalizeSceneHeadingForStorage} / `parseSceneHeading` in the draft editor API.
 */
export function composeSceneHeadingLineFromSlugFields(
  intExt: string,
  location: string,
  time: string
): string {
  const ie = normalizeIntExtForSlug(intExt);
  const loc = location.trim().toUpperCase();
  const ti = time.trim().toUpperCase();
  let line = ie;
  if (loc) {
    line = `${line} ${loc}`;
  }
  if (ti) {
    line = `${line} - ${ti}`;
  }
  return line.replace(/\s+/g, " ").trim().toUpperCase();
}

/** True when the scene_heading paragraph text disagrees with the schedule draft slug line. */
export function sceneHeadingParagraphDiffersFromScheduleDraft(
  paragraphs: DraftParagraph[],
  draft: Pick<SceneScheduleDraftFields, "int_ext" | "location" | "time">
): boolean {
  const composed = composeSceneHeadingLineFromSlugFields(draft.int_ext, draft.location, draft.time);
  if (!composed) return false;
  const idx = paragraphs.findIndex((p) => p.type === "scene_heading");
  if (idx === -1) return false;
  const current = paragraphs[idx]!.text.replace(/\s+/g, " ").trim().toUpperCase();
  return current !== composed;
}

export type SlugFieldEdits = {
  int_ext: boolean;
  location: boolean;
  time: boolean;
};

/**
 * Merges schedule-panel slug fields with the existing heading line before save.
 * Empty draft segments that the user did not edit are filled from the heading parse
 * so a time-only edit does not strip location text or send `location: ""` to the API.
 */
export function resolveSlugFieldsForHeadingSave(
  draft: Pick<SceneScheduleDraftFields, "int_ext" | "location" | "time">,
  existingHeadingText: string,
  slugFieldEdits?: Partial<SlugFieldEdits>
): Pick<SceneScheduleDraftFields, "int_ext" | "location" | "time"> {
  const parsed = parseSceneHeading(existingHeadingText);
  const draftIntExt = normalizeIntExtForSlug(draft.int_ext);
  const draftLoc = draft.location.trim().toUpperCase();
  const draftTime = draft.time.trim().toUpperCase();
  const parsedLoc = (parsed.location ?? "").trim().toUpperCase();
  const parsedIntExt = parsed.int_ext ? normalizeIntExtForSlug(parsed.int_ext) : "";
  const parsedTime = (parsed.time ?? "").trim().toUpperCase();

  let int_ext = draftIntExt;
  if (!int_ext && !slugFieldEdits?.int_ext) {
    int_ext = parsedIntExt;
  }

  let location = draftLoc;
  if (!location && !slugFieldEdits?.location) {
    location = parsedLoc;
  }

  let time = draftTime;
  if (!time && !slugFieldEdits?.time) {
    time = parsedTime;
  }

  return { int_ext, location, time };
}

/** True when Scene details slug fields differ from the loaded scene (needs explicit PATCH meta). */
export function scheduleDraftSlugDiffersFromScene(
  draft: Pick<SceneScheduleDraftFields, "int_ext" | "location" | "time">,
  scene: {
    scene_heading?: string | null;
    int_ext?: string | null;
    location?: string | null;
    time?: string | null;
  }
): boolean {
  const server = sceneScheduleDraftFromScene(scene);
  return (
    normalizeIntExtForSlug(draft.int_ext) !== server.int_ext ||
    draft.location.trim().toUpperCase() !== server.location ||
    draft.time.trim().toUpperCase() !== server.time
  );
}

/** True when a saved scene's slug columns and heading line match the schedule draft we sent. */
export function slugMetaSaveMatchesScheduleDraft(
  scene: {
    scene_heading?: string | null;
    int_ext?: string | null;
    location?: string | null;
    time?: string | null;
    paragraphs?: Array<{ type: string; text: string }>;
  },
  draft: Pick<SceneScheduleDraftFields, "int_ext" | "location" | "time">
): boolean {
  const composed = composeSceneHeadingLineFromSlugFields(draft.int_ext, draft.location, draft.time);
  const heading = scene.paragraphs?.find((p) => p.type === "scene_heading");
  const headingText = (heading?.text ?? scene.scene_heading ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
  if (headingText !== composed) return false;

  const server = sceneScheduleDraftFromScene(scene);
  return (
    normalizeIntExtForSlug(draft.int_ext) === server.int_ext &&
    draft.location.trim().toUpperCase() === server.location &&
    draft.time.trim().toUpperCase() === server.time
  );
}

/**
 * Ensures the scene_heading paragraph text matches the composed slug line before PATCH.
 * Prevents collab TipTap lag from sending a stale heading that reverts `time` on the server.
 */
export function applyComposedHeadingToParagraphs(
  paragraphs: DraftParagraph[],
  draft: Pick<SceneScheduleDraftFields, "int_ext" | "location" | "time">
): DraftParagraph[] {
  const composed = composeSceneHeadingLineFromSlugFields(draft.int_ext, draft.location, draft.time);
  if (!composed) return paragraphs;
  const idx = paragraphs.findIndex((p) => p.type === "scene_heading");
  if (idx === -1) return paragraphs;
  const row = paragraphs[idx]!;
  const current = row.text.replace(/\s+/g, " ").trim().toUpperCase();
  if (current === composed) return paragraphs;
  const next = [...paragraphs];
  next[idx] = { ...row, text: composed };
  return next;
}

export type SceneScheduleApiPayload = {
  page?: number | null;
  length?: string | null;
  length_is_manual?: boolean;
  scene_number?: string | null;
  summary?: string | null;
  int_ext?: string;
  location?: string;
  time?: string;
};

export type BuildSceneScheduleApiPayloadOptions = {
  /** When true, PATCH includes int_ext / location / time from the draft (user edited them in Scene details). */
  includeHeadingSlugMeta?: boolean;
  /** Slug fields after merging with the existing heading line (see `resolveSlugFieldsForHeadingSave`). */
  resolvedSlugFields?: Pick<SceneScheduleDraftFields, "int_ext" | "location" | "time">;
  /** Which slug fields the user edited in Scene details this save cycle. */
  slugFieldEdits?: Partial<SlugFieldEdits>;
};

/** Values for PATCH draft scene (production schedule fields). Omits `page` when the draft is non-empty but not a valid positive integer. */
export function buildSceneScheduleApiPayload(
  draft: SceneScheduleDraftFields,
  options?: BuildSceneScheduleApiPayloadOptions
): SceneScheduleApiPayload {
  const out: SceneScheduleApiPayload = {};
  const pageTrim = draft.page.trim();
  if (pageTrim === "") {
    out.page = null;
  } else {
    const n = Number(pageTrim);
    if (Number.isInteger(n) && n >= 1) {
      out.page = n;
    }
  }
  out.length = draft.length.trim() === "" ? null : draft.length.trim().slice(0, 10);
  out.length_is_manual = draft.length_is_manual;
  out.scene_number =
    draft.scene_number.trim() === "" ? null : draft.scene_number.trim().slice(0, 10);
  out.summary = draft.summary.trim() === "" ? null : draft.summary.trim().slice(0, 512);

  if (options?.includeHeadingSlugMeta === true) {
    const slug = options.resolvedSlugFields ?? draft;
    const edits = options.slugFieldEdits;
    const intExt = normalizeIntExtForSlug(slug.int_ext);
    if (intExt || edits?.int_ext) {
      out.int_ext = intExt.slice(0, 255);
    }
    if (edits?.location) {
      out.location = draft.location.trim().slice(0, 255).toUpperCase();
    } else if (slug.location.trim()) {
      out.location = slug.location.trim().slice(0, 255).toUpperCase();
    }
    if (slug.time.trim() || edits?.time) {
      out.time = slug.time.trim().slice(0, 50).toUpperCase();
    }
  }

  return out;
}
